import { orderBy, uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { selectWidths } from '~/common/constants/selectWidths';
import { useAppConfigContext } from '~/contexts/AppConfig';
import { Box } from '~/eds/src/components/Box/Box';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Select } from '~/eds/src/components/Select/Select';
import { useDebouncedCallback } from '~/hooks/useDebouncedCallback';
import { api } from '~/redux/api/createApi';
import { Supplier } from '~/redux/api/methods/workflows/types';
import * as selectors from '~/redux/selectors';
import { Nullable } from '~/types/Nullable';

import {
  SupplierOption,
  SupplierValue as SupplierValueType,
} from '../types/fields';
import { WorkflowQuestionFormField } from './types';
import { useIntegrationErrorToast } from './useIntegrationErrorToast';

export const SupplierWorkflowQuestion = ({
  question,
  onChange,
  ...rest
}: WorkflowQuestionFormField<SupplierValueType>) => {
  const intl = useIntl();
  const { isPanelEnlarged } = useAppConfigContext();
  const { workflowId } = useSelector(selectors.selectIntakeForm) ?? {};

  const {
    field: { value },
  } = question;

  const [selectedSupplier, setSelectedSupplier] =
    useState<Nullable<SupplierOption>>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [
    getSuppliers,
    { data: suppliers = [], isFetching, error: fetchError, isSuccess },
  ] = api.endpoints.getIntegrateWorkflowSuppliers.useLazyQuery();

  const debouncedGetSuppliers = useDebouncedCallback(getSuppliers, null);

  const options: SupplierOption[] = useMemo(() => {
    if (searchQuery.length < 2) {
      return [];
    }
    return suppliers.map((supplier: Supplier) => ({
      label: supplier.name,
      value: supplier.id,
    }));
  }, [suppliers, searchQuery]);

  const { connectionErrorMessage } = useIntegrationErrorToast({
    isError: Boolean(fetchError),
    isSuccess,
  });

  useEffect(() => {
    if (value?.value?.resource_id && value?.value?.default_value) {
      setSelectedSupplier({
        label: value.value.default_value,
        value: value.value.resource_id,
      });
    } else {
      setSelectedSupplier(null);
    }
  }, [value?.value?.resource_id, value?.value?.default_value]);

  const optionsWithSelected = useMemo(() => {
    const allOptions = selectedSupplier
      ? [selectedSupplier, ...options]
      : options;
    return orderBy(uniqBy(allOptions, 'value'), ['label'], ['asc']);
  }, [options, selectedSupplier]);

  const handleChange = useCallback(
    (selectedValue: string | string[] | null) => {
      const valueString = Array.isArray(selectedValue)
        ? selectedValue[0]
        : selectedValue;

      const selectedOption = valueString
        ? optionsWithSelected.find((opt) => opt.value === valueString)
        : null;

      setSelectedSupplier(selectedOption || null);

      if (selectedOption) {
        onChange(
          {
            default_value: selectedOption.label,
            resource_id: selectedOption.value,
          },
          'value',
        );
      } else {
        onChange(null, 'value');
      }
    },
    [onChange, optionsWithSelected],
  );

  const handleSearch = useCallback(
    (searchQuery: string) => {
      setSearchQuery(searchQuery);
      if (workflowId) {
        if (searchQuery.length >= 2) {
          debouncedGetSuppliers({
            workflowId,
            searchString: searchQuery,
          });
        } else {
          debouncedGetSuppliers.cancel?.();
        }
      }
    },
    [workflowId, debouncedGetSuppliers],
  );

  return (
    <Box w={isPanelEnlarged ? selectWidths.l : selectWidths.fullWidth}>
      <FormField
        {...rest}
        input={Select}
        placeholder={intl.formatMessage({
          id: 'SupplierWorkflowQuestion.placeholder',
          defaultMessage: 'Search and select a supplier...',
          description: 'Placeholder text for supplier selection in intake form',
        })}
        value={selectedSupplier?.value ?? null}
        onChange={handleChange}
        error={connectionErrorMessage ? undefined : rest.error}
        footer={connectionErrorMessage}
        inputProps={{
          options: optionsWithSelected,
          isLoading: isFetching,
          onSearch: handleSearch,
          enablePortal: true,
          isMulti: false,
          width: '100%',
          noOptionsMessage:
            connectionErrorMessage ||
            intl.formatMessage({
              id: 'SupplierWorkflowQuestion.noOptionsMessage',
              defaultMessage: 'No suppliers found',
              description:
                'Message shown when no suppliers match the search query',
            }),
        }}
      />
    </Box>
  );
};
